/**
 * SignInFormContent - Shared sign-in form logic and UI
 *
 * Contains Google sign-in, magic link form, and success state.
 * Used by both LoginModal and LoginPage.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { logError } from '@/lib/logger';

// Icons
const GoogleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g fill="none" fillRule="evenodd">
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </g>
  </svg>
);

const PaperPlaneIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#228B22"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
  </svg>
);

const BackArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
      strokeDasharray="60"
      strokeDashoffset="20"
      opacity="0.3"
    />
  </svg>
);

// Sub-components
const ErrorMessage = ({ message }: { message: string }) => (
  <div
    className="mt-3 p-3 rounded-lg text-sm"
    style={{
      background: 'rgba(196, 30, 58, 0.1)',
      color: '#C41E3A',
      border: '1px solid rgba(196, 30, 58, 0.2)',
    }}
  >
    {message}
  </div>
);

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 text-sm font-medium mb-4 transition-colors"
    style={{ color: '#8B2332' }}
  >
    <BackArrowIcon />
    Back
  </button>
);

interface SignInFormContentProps {
  signIn: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<void>;
  /** Input field ID prefix to avoid conflicts when multiple forms exist */
  idPrefix?: string;
}

export function SignInFormContent({
  signIn,
  signInWithEmail,
  idPrefix = 'signin',
}: SignInFormContentProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      await signIn();
    } catch (err) {
      logError('Google sign in error', err);
      setError('Failed to sign in with Google. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleMagicLinkSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await signInWithEmail(email.trim());
      setMagicLinkSent(true);
    } catch (err) {
      logError('Magic link sign in error', err);
      setError('Failed to send magic link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setMagicLinkSent(false);
    setShowEmailInput(false);
    setEmail('');
    setError(null);
  };

  // Success state - Magic link sent
  if (magicLinkSent) {
    return (
      <div className="text-center py-2">
        <div
          className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(34, 139, 34, 0.1)' }}
        >
          <PaperPlaneIcon />
        </div>
        <h3
          className="font-serif text-lg font-medium mb-2"
          style={{ color: '#1A1A1A' }}
        >
          Check your email
        </h3>
        <p
          className="text-sm mb-3 leading-relaxed"
          style={{ color: '#6B6B6B' }}
        >
          We've sent a one-time login link to
          <br />
          <span style={{ color: '#1A1A1A', fontWeight: 500 }}>{email}</span>
        </p>
        <p className="text-xs" style={{ color: '#A0A0A0' }}>
          Click the link in the email to sign in.
        </p>
        <button
          onClick={resetForm}
          className="mt-4 text-sm font-medium transition-colors"
          style={{ color: '#8B2332' }}
        >
          ← Back to sign in
        </button>
      </div>
    );
  }

  // Email input state
  if (showEmailInput) {
    return (
      <>
        <BackButton
          onClick={() => {
            setShowEmailInput(false);
            setError(null);
          }}
        />

        <form onSubmit={handleMagicLinkSignIn}>
          <label
            htmlFor={`${idPrefix}-email`}
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Enter your email to receive a one-time link
          </label>
          <input
            id={`${idPrefix}-email`}
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoFocus
            className="w-full px-4 py-3 rounded-lg text-base transition-all duration-200"
            style={{
              background: '#FFFFFF',
              border: '2px solid #E8E0D8',
              color: '#1A1A1A',
              outline: 'none',
            }}
            onFocus={e => {
              e.currentTarget.style.borderColor = '#8B2332';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = '#E8E0D8';
            }}
            disabled={isSubmitting}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-3 flex items-center justify-center gap-3 px-6 py-3.5 rounded-lg font-medium transition-all duration-200"
            style={{
              background: '#8B2332',
              color: '#FFFFFF',
              opacity: isSubmitting ? 0.7 : 1,
              cursor: isSubmitting ? 'wait' : 'pointer',
            }}
            onMouseEnter={e => {
              if (!isSubmitting) {
                e.currentTarget.style.background = '#6B1A27';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#8B2332';
            }}
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon />
                <span>Sending link...</span>
              </>
            ) : (
              <span>Send one-time link</span>
            )}
          </button>
        </form>

        {error && <ErrorMessage message={error} />}
      </>
    );
  }

  // Main sign-in options
  return (
    <>
      {/* Google sign in */}
      <button
        onClick={handleGoogleSignIn}
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-lg font-medium transition-all duration-200"
        style={{
          background: '#FFFFFF',
          border: '2px solid #E8E0D8',
          color: '#1A1A1A',
          opacity: isSubmitting ? 0.6 : 1,
          cursor: isSubmitting ? 'wait' : 'pointer',
        }}
        onMouseEnter={e => {
          if (!isSubmitting) {
            e.currentTarget.style.borderColor = '#8B2332';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(139,35,50,0.15)';
          }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = '#E8E0D8';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <GoogleIcon />
        <span>Continue with Google</span>
      </button>

      {error && <ErrorMessage message={error} />}

      {/* Divider */}
      <div className="flex items-center gap-4 my-5">
        <div className="flex-1 h-px" style={{ background: '#E8E0D8' }} />
        <span className="text-xs" style={{ color: '#A0A0A0' }}>
          or
        </span>
        <div className="flex-1 h-px" style={{ background: '#E8E0D8' }} />
      </div>

      {/* One-time link option */}
      <div className="text-center">
        <button
          onClick={() => setShowEmailInput(true)}
          className="text-sm font-medium transition-colors hover:underline"
          style={{ color: '#8B2332' }}
        >
          Sign in with a one-time link
        </button>
      </div>

      <div
        className="mt-5 pt-4 text-center text-xs leading-relaxed"
        style={{ borderTop: '1px solid #E8E0D8', color: '#A0A0A0' }}
      >
        By signing in, you agree to our{' '}
        <Link to="/terms" className="underline" style={{ color: '#8B2332' }}>
          terms of service
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="underline" style={{ color: '#8B2332' }}>
          privacy policy
        </Link>
        .
      </div>
    </>
  );
}
